<template>
	<div class="ks-video-grid-container">
		<div
			v-for="item in ordered_items"
			class="ks-video-grid-item"
			:class="item_classes(item)"
			:style="item_style(item)"
			:key="item_key(item)"
			:draggable="item_draggable(item)"
			@mousedown="item_mousedown($event, item)"
			@mouseenter.capture="item_mouseenter"
			@mouseup="reorder_key = null"
			@dragstart="item_dragstart($event, item)"
			@dragover="item_dragover($event, item)"
			@dragleave="item_dragleave($event, item)"
			@drop="item_drop($event, item)"
			@dragend="item_dragend"
		>
			<slot :item="item"></slot>
			<div
				v-if="can_resize"
				class="ks-video-grid-resize-handle"
				v-tooltip.bottom="'Shift + drag: resize all<br>Double-click: match to previous'"
				@pointerdown.prevent.stop="start_drag($event, item)"
				@pointermove="drag($event, item)"
				@pointerup="end_drag($event)"
				@pointercancel="end_drag($event)"
				@lostpointercapture="end_drag($event)"
				@dblclick.stop="match_previous_size($event, item)"
			></div>
		</div>
	</div>
</template>

<script>
export default {
	props: {
		"items": {
			type: Array,
			default: function() {return []}
		},
		"disable-resizing": {
			type: Boolean,
			default: false
		},
		"disable-reordering": {
			type: Boolean,
			default: false
		},
		// When set, sizes and order are saved to localStorage under this key (per account)
		"storage-key": {
			type: String,
			default: null
		},
		// Max number of sizes/order entries saved to localStorage (0 = no limit)
		"max-entries": {
			type: Number,
			default: 0
		}
	},
	data () {
		var saved = this.load_layout();
		return {
			sizes: saved.sizes,
			last_size: saved.last_size,
			// Keys with a restored size that have not appeared yet in this session
			restored_keys: Object.assign({}, saved.sizes),
			known_keys: {},
			drag_state: null,
			storage_warned: false,
			order: saved.order,
			reorder_key: null,
			dragged_key: null,
			drop_target: null
		}
	},
	watch: {
		items: {
			immediate: true,
			handler (items) {
				var known = {};
				items.forEach(i => {
					var key = this.item_key(i);
					known[key] = true;
					if (!this.known_keys[key]) {
						if (this.restored_keys[key] !== undefined) {
							// First appearance after a page load: keep the restored size
							delete this.restored_keys[key];
						} else if (this.last_size && !this.sizes[key]) {
							this.$set(this.sizes, key, this.last_size);
						}
					}
				});
				this.known_keys = known;
			}
		}
	},
	computed: {
		can_resize () {
			return !this.disableResizing && !this.is_compact();
		},
		can_reorder () {
			return !this.disableReordering && !this.is_compact();
		},
		ordered_items () {
			if (!this.can_reorder || !this.order.length) return this.items;
			var pos = {};
			this.order.forEach((k, i) => { pos[k] = i; });
			var known = [];
			var unknown = [];
			this.items.forEach(i => {
				(pos[this.item_key(i)] !== undefined ? known : unknown).push(i);
			});
			known.sort((a, b) => pos[this.item_key(a)] - pos[this.item_key(b)]);
			return known.concat(unknown);
		}
	},
	methods: {
		// Reset buttons in the sidebars remove this same key: "kvideogrid:<account id>:<storage-key>"
		storage_name () {
			if (!this.storageKey) return null;
			var account = (this.$store && this.$store.state && this.$store.state.account) || {};
			return "kvideogrid:" + (account.id || "") + ":" + this.storageKey;
		},
		clamp_width (v) {
			v = Number(v);
			if (!isFinite(v)) return null;
			return Math.round(Math.min(1000, Math.max(300, v)));
		},
		// Called from data(), so it must not touch reactive state
		load_layout () {
			var result = {sizes: {}, last_size: null, order: []};
			var name = this.storage_name();
			if (!name) return result;
			try {
				var data = JSON.parse(window.localStorage.getItem(name) || "null");
				if (!data || typeof data !== "object") return result;
				if (data.sizes && typeof data.sizes === "object") {
					Object.keys(data.sizes).forEach(k => {
						var w = this.clamp_width(data.sizes[k]);
						if (w !== null) result.sizes[k] = w;
					});
				}
				result.last_size = this.clamp_width(data.last_size);
				if (Array.isArray(data.order)) {
					result.order = data.order.filter(k => typeof k === "string" || typeof k === "number");
				}
			} catch (err) {
				// Corrupt or unavailable storage: start with defaults
			}
			return result;
		},
		// Called when a resize or reorder action has finished
		save_layout () {
			var name = this.storage_name();
			if (!name || (!this.can_resize && !this.can_reorder)) return;
			var keys = Object.keys(this.sizes);
			var order = this.order;
			// Optionally keep only the most recently added entries so stale keys (e.g. old events) cannot grow forever
			if (this.maxEntries > 0) {
				keys = keys.slice(-this.maxEntries);
				order = order.slice(-this.maxEntries);
			}
			var sizes = {};
			keys.forEach(k => { sizes[k] = this.sizes[k]; });
			try {
				window.localStorage.setItem(name, JSON.stringify({
					sizes: sizes,
					last_size: this.last_size,
					order: order
				}));
			} catch (err) {
				// Storage full or unavailable: warn once so repeated drags don't spam notifications
				if (!this.storage_warned && this.$message) {
					this.storage_warned = true;
					this.$message.warning("Browser storage is full. Layout changes (size and order) could not be saved.");
				}
			}
		},
		// Returns null (attribute omitted) unless a reorder drag is being prepared
		item_draggable (item) {
			return (this.can_reorder && this.reorder_key === this.item_key(item)) ? true : null;
		},
		item_classes (item) {
			if (!this.can_reorder) return null;
			var key = this.item_key(item);
			var t = this.drop_target;
			return {
				"ks-video-grid-item-reorderable": true,
				"ks-video-grid-item-dragging": this.dragged_key === key,
				"ks-video-grid-item-drop-before": !!t && t.key === key && !t.after,
				"ks-video-grid-item-drop-after": !!t && t.key === key && t.after
			};
		},
		// Reordering can only be started from the panel heading (not from its buttons)
		close_tooltips (root) {
			if (!root || !root.querySelectorAll) return;
			root.querySelectorAll("*").forEach(el => {
				if (el.__vueTooltipLeave__) el.__vueTooltipLeave__();
			});
		},
		// Capture-phase handler: stops tooltip directives inside the heading from seeing mouseenter
		item_mouseenter (e) {
			if (!this.can_reorder) return;
			var t = e.target;
			if (t && t.closest && t.closest(".ks-panel-heading")) e.stopPropagation();
		},
		item_mousedown (e, item) {
			this.reorder_key = null;
			if (!this.can_reorder || e.button !== 0) return;
			var t = e.target;
			if (!t.closest || !t.closest(".ks-panel-heading")) return;
			if (t.closest("button, a, input, [role=button], .ks-btn")) return;
			this.close_tooltips(e.currentTarget);
			this.reorder_key = this.item_key(item);
		},
		item_dragstart (e, item) {
			var key = this.item_key(item);
			if (!this.can_reorder || this.reorder_key !== key) {
				e.preventDefault();
				return;
			}
			e.dataTransfer.effectAllowed = "move";
			this.close_tooltips(e.currentTarget);
			e.dataTransfer.setData("text/plain", ""); // required by Firefox
			// Delay so the drag image is taken before the item is dimmed
			setTimeout(() => { this.dragged_key = key; }, 0);
		},
		item_dragover (e, item) {
			if (!this.can_reorder) return;
			if (this.dragged_key === null && this.reorder_key === null) return;
			e.preventDefault();
			e.dataTransfer.dropEffect = "move";
			var key = this.item_key(item);
			if (key === this.dragged_key) {
				this.drop_target = null;
				return;
			}
			var rect = e.currentTarget.getBoundingClientRect();
			var after = e.clientX > rect.left + rect.width / 2;
			var t = this.drop_target;
			if (!t || t.key !== key || t.after !== after) this.drop_target = {key: key, after: after};
		},
		item_dragleave (e, item) {
			if (e.currentTarget.contains(e.relatedTarget)) return;
			if (this.drop_target && this.drop_target.key === this.item_key(item)) this.drop_target = null;
		},
		item_drop (e, item) {
			if (!this.can_reorder || this.dragged_key === null) return;
			e.preventDefault();
			var t = this.drop_target;
			var from = this.dragged_key;
			this.item_dragend();
			if (!t || from === null) return;
			var keys = this.ordered_items.map(i => this.item_key(i)).filter(k => k !== from);
			var idx = keys.indexOf(t.key);
			if (idx < 0) return;
			keys.splice(idx + (t.after ? 1 : 0), 0, from);
			this.order = keys;
			this.save_layout();
		},
		item_dragend () {
			this.dragged_key = null;
			this.drop_target = null;
			this.reorder_key = null;
		},
		is_compact () {
			return !!(this.$compact_view && this.$compact_view());
		},
		item_style (item) {
			var width = this.sizes[this.item_key(item)];
			if (!width || !this.can_resize) return null;
			return {"--ks-video-grid-item-width": width + "px"};
		},
		start_drag (e, item) {
			if (e.button !== 0) return;
			// Close tooltip (and cancel its pending show) when resizing starts
			if (e.target.__vueTooltipLeave__) e.target.__vueTooltipLeave__();
			var el = e.target.parentElement;
			e.target.setPointerCapture(e.pointerId);
			this.drag_state = {
				key: this.item_key(item),
				pointer_id: e.pointerId,
				start_x: e.clientX,
				start_width: el.getBoundingClientRect().width
			};
		},
		drag (e, item) {
			var s = this.drag_state;
			if (!s || s.key !== this.item_key(item) || s.pointer_id !== e.pointerId) return;
			// Button released without pointerup reaching us (e.g. trackpad): stop dragging
			if (e.buttons === 0) {
				this.end_drag(e);
				return;
			}
			var width = Math.round(Math.min(1000, Math.max(300, s.start_width + e.clientX - s.start_x)));
			this.last_size = width;
			if (e.shiftKey) {
				this.items.forEach(i => this.$set(this.sizes, this.item_key(i), width));
			} else {
				this.$set(this.sizes, s.key, width);
			}
		},
		end_drag (e) {
			if (!this.drag_state) return;
			this.drag_state = null;
			this.save_layout();
			if (e.target.hasPointerCapture && e.target.hasPointerCapture(e.pointerId)) {
				e.target.releasePointerCapture(e.pointerId);
			}
		},
		match_previous_size (e, item) {
			var prev = e.currentTarget.parentElement.previousElementSibling;
			if (!prev) {
				this.reset_size(item);
				return;
			}
			var width = Math.round(prev.getBoundingClientRect().width);
			this.last_size = width;
			this.$set(this.sizes, this.item_key(item), width);
			this.save_layout();
		},
		reset_size (item) {
			this.$delete(this.sizes, this.item_key(item));
			this.save_layout();
		},
		item_key (item) {
			return item.id || item.uuid;
		}
	}
}
</script>
<style scoped>
.ks-video-grid-container {
	display: flex;
	flex-wrap: wrap;
	align-items: flex-start;
	gap: 5px; /* Väli videoiden väliin */
}
.ks-video-grid-item {
	position: relative;
	isolation: isolate;
	box-sizing: border-box;
	width: var(--ks-video-grid-item-width, 300px);
	flex: 0 0 auto;
}
.ks-video-grid-item-dragging {
	opacity: 0.4;
}
.ks-video-grid-item-drop-before {
	box-shadow: -3px 0 0 0 var(--ks-col-primary, #4a90d9);
}
.ks-video-grid-item-drop-after {
	box-shadow: 3px 0 0 0 var(--ks-col-primary, #4a90d9);
}
.ks-video-grid-resize-handle {
	position: absolute;
	right: 0;
	bottom: 0;
	width: 16px;
	height: 16px;
	cursor: nwse-resize;
	touch-action: none;
	z-index: 1;
	opacity: 0.5;
	background: linear-gradient(135deg, transparent 50%, var(--ks-col-secondary, #888) 50%, var(--ks-col-secondary, #888) 60%, transparent 60%, transparent 75%, var(--ks-col-secondary, #888) 75%, var(--ks-col-secondary, #888) 85%, transparent 85%);
}
.ks-video-grid-resize-handle:hover {
	opacity: 1;
}
@media (max-width: 768px) {
	.ks-video-grid-resize-handle {
		display: none;
	}
	.ks-video-grid-item {
		width: 100%;
	}
}
</style>
<style>
/* Not scoped: the heading belongs to a child component */
.ks-video-grid-item-reorderable .ks-panel-heading {
	cursor: grab;
}
.ks-video-grid-item-reorderable .ks-panel-heading button,
.ks-video-grid-item-reorderable .ks-panel-heading a,
.ks-video-grid-item-reorderable .ks-panel-heading .ks-btn {
	cursor: pointer;
}
</style>